import type { Dispatch, SetStateAction } from 'react';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { browseForFolderGrpc } from '../../../shared/api/grpc/revitClient';
import { exportActionTooltips } from '../../../shared/config/actionHelpCatalog';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import type { ExportPathDialogState } from '../hooks/useExportWorkspaceCommands';

interface ExportOutputPathDialogProps {
  state: ExportPathDialogState;
  setState: Dispatch<SetStateAction<ExportPathDialogState>>;
  onClose: () => void;
  onConfirm: () => void;
}

export default function ExportOutputPathDialog(props: ExportOutputPathDialogProps) {
  return (
    <Dialog open={props.state.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export output path</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the folder path where PCF files will be saved.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          placeholder="C:\\Export\\PCF"
          value={props.state.value}
          onChange={(event) => props.setState((previous) => ({ ...previous, value: event.target.value }))}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && props.state.value.trim()) {
              props.onConfirm();
            }
          }}
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <ActionHelpTooltip help={exportActionTooltips.browseOutputPath}>
                  <IconButton
                    size="small"
                    aria-label="Browse output folder"
                    onClick={async () => {
                      const result = await browseForFolderGrpc(props.state.value, 'Select PCF export folder');
                      if (result.success && result.path?.trim()) {
                        props.setState((previous) => ({ ...previous, value: result.path!.trim() }));
                      }
                    }}
                  >
                    <FolderOpenIcon fontSize="small" />
                  </IconButton>
                </ActionHelpTooltip>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button variant="contained" disabled={!props.state.value.trim()} onClick={props.onConfirm}>
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
}
